'use client';

import * as React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { ReactElement, ReactNode } from 'react';
import { Modal } from '../primitives/Modal';
import { ConfirmBody } from './ConfirmBody';

/* Imperative confirm / alert dialogs — replace window.confirm / alert.
   B1 decision: Modal is now a declarative createPortal component, so the
   imperative surface queues renders through a single React root mounted
   in a hidden host under document.body. Sequential awaits and concurrent
   Promise.all() both present in registration order.

   SSR contract: calling confirmDialog / alertDialog in a non-DOM
   environment (Node, RSC, unit tests that didn't pull in jsdom) returns
   a resolved promise rather than crashing. Mirrors how window.confirm
   / alert would behave on an embedded headless runtime that doesn't
   expose them. */

interface DialogRequest {
  render: (done: (value: unknown) => void) => ReactElement;
  resolve: (value: unknown) => void;
  /* Per-request fallback captured at enqueue time. Used by
     teardownDialogs({ force: true }) so each in-flight / queued
     promise resolves with the shape its caller expected:
     confirmDialog → false, alertDialog → true. The earlier
     shared-fallback-of-false branch violated alertDialog's
     Promise<true> type at runtime — typed consumers would write
     `const x: true = await alertDialog()` and get false back
     with no compile warning. */
  fallback: unknown;
}

let _dialogRoot: Root | null = null;
const _dialogQueue: DialogRequest[] = [];
let _dialogRunning = false;
/* The in-flight dialog's request lives here so teardownDialogs({
   force: true }) can resolve it. Without this, a logout-triggered
   force-teardown leaves the currently-rendering dialog's promise
   hanging — the caller awaits forever even though the UI is gone. */
let _activeRequest: DialogRequest | null = null;

function hasDOM(): boolean {
  return typeof document !== 'undefined' && typeof document.createElement === 'function';
}

function getDialogHost(): HTMLElement {
  let host = document.getElementById('jkit-dialog-host');
  if (!host) {
    host = document.createElement('div');
    host.id = 'jkit-dialog-host';
    document.body.appendChild(host);
  }
  return host;
}

function getDialogRoot(): Root {
  if (_dialogRoot) return _dialogRoot;
  _dialogRoot = createRoot(getDialogHost());
  return _dialogRoot;
}

function drainDialogQueue(): void {
  if (_dialogRunning) return;
  const next = _dialogQueue.shift();
  if (!next) return;
  _dialogRunning = true;
  _activeRequest = next;
  const root = getDialogRoot();
  const cleanup = (result: unknown) => {
    root.render(null);
    next.resolve(result);
    _dialogRunning = false;
    _activeRequest = null;
    if (_dialogQueue.length === 0) {
      /* Queue drained. Unmount the root + detach the host on a
         microtask so a long-lived SPA doesn't hold an unused
         React root across sessions, and so Fast Refresh doesn't
         trip the "createRoot on a container already passed to
         createRoot" warning when the module reloads.

         Deferred via queueMicrotask (or Promise.resolve) because
         root.unmount() called synchronously inside an event
         handler that just triggered a render commits a warning
         in React 18 ("synchronously unmount while rendering")
         and races the Modal's cleanup effect (scrollLock release
         + inert clear). Microtask scheduling lets React's own
         commit cycle + effect cleanup finish before we drop the
         root on the floor. */
      scheduleTeardown();
    } else {
      drainDialogQueue();
    }
  };
  root.render(next.render(cleanup));
}

function scheduleTeardown(): void {
  const run = () => {
    /* Re-check queue state — something might have been enqueued
       between the render(null) and the microtask firing. */
    if (_dialogRunning || _dialogQueue.length > 0) return;
    teardownDialogs();
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(run);
  else Promise.resolve().then(run);
}

/* Exported for apps that want to proactively reclaim the dialog
   host on navigation / logout. Two modes:
   - Default (opts.force !== true): no-op while a dialog is
     rendering; the active cleanup tears down naturally.
   - force: true: resolve every pending request with the fallback
     value (false for confirms, true for alerts — same shape as
     the SSR path) and synchronously unmount. Use on logout /
     hard route transitions where you don't want `await
     confirmDialog(...)` call sites hanging forever. */
export interface TeardownDialogsOptions {
  force?: boolean;
}

export function teardownDialogs(opts: TeardownDialogsOptions = {}): void {
  if (_dialogRunning && !opts.force) return;
  if (opts.force) {
    /* Resolve the in-flight request FIRST with its per-request
       fallback — alertDialog sets fallback=true so Promise<true>
       is honoured; confirmDialog sets fallback=false. */
    if (_activeRequest) {
      _activeRequest.resolve(_activeRequest.fallback);
      _activeRequest = null;
    }
    /* Then drain any queued-but-not-yet-rendered requests with
       their own per-request fallbacks. No more lying to
       alertDialog's type. */
    for (const req of _dialogQueue.splice(0)) {
      req.resolve(req.fallback);
    }
    _dialogRunning = false;
  }
  if (_dialogRoot) {
    try {
      _dialogRoot.unmount();
    } catch {
      /* Root may have been unmounted already (e.g. HMR); swallow. */
    }
    _dialogRoot = null;
  }
  if (typeof document !== 'undefined') {
    const host = document.getElementById('jkit-dialog-host');
    host?.remove();
  }
}

function mountDialog<T>(
  render: (done: (value: T) => void) => ReactElement,
  fallback: T,
): Promise<T> {
  if (!hasDOM()) return Promise.resolve(fallback);
  return new Promise<T>((resolve) => {
    _dialogQueue.push({
      render: render as (done: (v: unknown) => void) => ReactElement,
      resolve: resolve as (v: unknown) => void,
      fallback: fallback as unknown,
    });
    drainDialogQueue();
  });
}

export interface ConfirmDialogOptions {
  title?: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
}

export function confirmDialog({
  title = 'Are you sure?',
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
}: ConfirmDialogOptions = {}): Promise<boolean> {
  /* APG §alertdialog parity with useConfirm: imperative confirms get
     the same role + aria-describedby wiring so the two surfaces
     behave identically. The id only matters when body is set;
     ariaDescribedBy is omitted otherwise so AT doesn't point at a
     missing element (codex Modal r1). */
  const bodyId = body != null ? 'jkit-imp-confirm-body' : undefined;
  return mountDialog<boolean>(
    (done) => (
      <Modal
        open
        onClose={() => done(false)}
        title={title}
        width={440}
        role="alertdialog"
        ariaDescribedBy={bodyId}
      >
        <ConfirmBody
          id={bodyId}
          body={body}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          destructive={destructive}
          onConfirm={() => done(true)}
          onCancel={() => done(false)}
        />
      </Modal>
    ),
    /* SSR fallback: no DOM, no user confirmation possible → resolve
       false so a `const ok = await confirmDialog(…); if (ok) …`
       call site doesn't silently trigger the confirmed branch. */
    false,
  );
}

export interface AlertDialogOptions {
  title?: string;
  body?: ReactNode;
  okLabel?: string;
}

export function alertDialog({
  title = 'Notice',
  body,
  okLabel = 'OK',
}: AlertDialogOptions = {}): Promise<true> {
  /* Same body-presence guard as confirmDialog (codex Modal r1). */
  const bodyId = body != null ? 'jkit-imp-alert-body' : undefined;
  return mountDialog<true>(
    (done) => (
      <Modal
        open
        onClose={() => done(true)}
        title={title}
        width={440}
        role="alertdialog"
        ariaDescribedBy={bodyId}
      >
        {/* alertDialog reuses ConfirmBody with cancelLabel={null} to
            hide the Cancel slot — alerts have one action only. */}
        <ConfirmBody
          id={bodyId}
          body={body}
          cancelLabel={null}
          confirmLabel={okLabel}
          onConfirm={() => done(true)}
          onCancel={() => done(true)}
        />
      </Modal>
    ),
    /* SSR fallback: alerts are informational, so resolve true to
       match how a real acknowledged dialog would resolve. Keeps
       `await alertDialog(…)` chaining happy on the server. */
    true,
  );
}
